import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UsersThree, PaperPlaneTilt, Check, Clock, Copy, LinkSimple, Gift, Crown } from "@phosphor-icons/react";
import { logWarn } from "@/lib/logger";

const emptyForm = { email: "", name: "", relationship: "", note: "" };

export default function Invite() {
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]);
  const [totals, setTotals] = useState({ total: 0, joined: 0 });
  const [copied, setCopied] = useState("");
  const [progress, setProgress] = useState(null);

  const load = async () => {
    const [{ data }, prog] = await Promise.all([
      api.get("/referrals/mine"),
      api.get("/referrals/progress").then((r) => r.data).catch(() => null),
    ]);
    setInvites(data.invites || []);
    setTotals({ total: data.total || 0, joined: data.joined || 0 });
    setProgress(prog);
  };

  useEffect(() => { load(); }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSending(true);
    try {
      await api.post("/referrals/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(""), 1500);
      toast.success("Link copied");
    } catch (e) {
      toast.error("Couldn't copy — please copy manually.");
      logWarn("clipboard.writeText failed:", e?.message || e);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid="invite-page">
      <div className="flex items-center gap-3">
        <UsersThree size={28} weight="duotone" className="text-brand-terracotta" />
        <div>
          <div className="label-eyebrow text-brand-terracotta">Grow the family</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-brand-ink">Invite family &amp; friends</h1>
        </div>
      </div>
      <p className="mt-4 text-brand-muted max-w-2xl">
        Invite parents, siblings, or friends who are searching. Every invitation
        we send is discreet — no marketing spam, only a personal introduction.
      </p>

      {/* Referral Reward Banner — 3 friends → 1 month Silver free */}
      {progress && <RewardBanner progress={progress} />}

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        <Stat label="Invitations sent" value={totals.total} />
        <Stat label="Joined WR Rishtey" value={totals.joined} />
        <Stat label="Pending" value={Math.max(0, totals.total - totals.joined)} />
      </div>

      <div className="mt-10 grid grid-cols-12 gap-8">
        {/* Form */}
        <form onSubmit={submit} className="col-span-12 lg:col-span-5 bg-white border border-brand-line p-8 h-fit space-y-5" data-testid="invite-form">
          <div className="label-eyebrow text-brand-terracotta">Send a personal invitation</div>
          <div>
            <Label className="text-xs text-brand-muted">Their email</Label>
            <Input
              type="email" required value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              data-testid="invite-email"
              className="mt-1.5 h-11 rounded-none border-brand-line"
              placeholder="parent@family.com"
            />
          </div>
          <div>
            <Label className="text-xs text-brand-muted">Their name (optional)</Label>
            <Input
              value={form.name} onChange={(e) => upd("name", e.target.value)}
              data-testid="invite-name"
              className="mt-1.5 h-11 rounded-none border-brand-line"
              placeholder="Priya Iyer"
            />
          </div>
          <div>
            <Label className="text-xs text-brand-muted">Relationship (optional)</Label>
            <Select value={form.relationship || ""} onValueChange={(v) => upd("relationship", v)}>
              <SelectTrigger data-testid="invite-relationship" className="mt-1.5 h-11 rounded-none border-brand-line">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="parent">Parent</SelectItem>
                <SelectItem value="sibling">Sibling</SelectItem>
                <SelectItem value="cousin">Cousin</SelectItem>
                <SelectItem value="friend">Friend</SelectItem>
                <SelectItem value="relative">Other relative</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-brand-muted">Personal note (optional)</Label>
            <Textarea
              rows={4} value={form.note}
              onChange={(e) => upd("note", e.target.value)}
              data-testid="invite-note"
              className="rounded-none border-brand-line"
              placeholder="I've had a lovely experience here — thought you should see it."
            />
          </div>
          <Button
            type="submit" disabled={sending}
            data-testid="invite-submit"
            className="w-full h-12 rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white"
          >
            <PaperPlaneTilt size={16} className="mr-2" weight="fill" />
            {sending ? "Sending…" : "Send invitation"}
          </Button>
          <p className="text-[11px] text-brand-muted leading-relaxed">
            We'll send them a beautifully-designed email from WR Rishtey with your note. They can decline politely, and we never spam.
          </p>
        </form>

        {/* Invites list */}
        <div className="col-span-12 lg:col-span-7">
          <div className="label-eyebrow text-brand-muted mb-4">Your invitations</div>
          {invites.length === 0 ? (
            <div className="border border-dashed border-brand-line p-16 text-center text-brand-muted" data-testid="invites-empty">
              You haven't sent any invitations yet. Start with one on the left.
            </div>
          ) : (
            <div className="border border-brand-line bg-white divide-y divide-brand-line">
              {invites.map((inv) => (
                <div key={inv.id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`invite-row-${inv.id}`}>
                  <div className="w-11 h-11 rounded-full bg-brand-bone flex items-center justify-center flex-shrink-0">
                    {inv.status === "joined"
                      ? <Check size={18} weight="bold" className="text-emerald-600" />
                      : <Clock size={18} className="text-brand-gold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-brand-ink truncate">
                      {inv.invitee_name || inv.invitee_email}
                      {inv.relationship && <span className="text-brand-muted text-xs ml-2 capitalize">· {inv.relationship}</span>}
                    </div>
                    <div className="text-xs text-brand-muted truncate">{inv.invitee_email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                    inv.status === "joined" ? "bg-emerald-100 text-emerald-700" : "bg-brand-gold/20 text-brand-gold"
                  }`}>{inv.status === "joined" ? "Joined" : "Pending"}</span>
                  <button
                    type="button"
                    onClick={() => copy(inv.invite_url, inv.id)}
                    data-testid={`copy-invite-${inv.id}`}
                    className="p-2 hover:bg-brand-bone rounded-full flex items-center gap-1 text-xs text-brand-muted"
                    title="Copy invite link"
                  >
                    {copied === inv.id ? <Check size={14} className="text-emerald-600" /> : <LinkSimple size={14} />}
                    <span className="hidden md:inline">Copy link</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-brand-line p-6">
      <div className="font-serif text-5xl text-brand-ink tracking-tighter num-lining">{value}</div>
      <div className="mt-1 label-eyebrow text-brand-muted">{label}</div>
    </div>
  );
}

function RewardBanner({ progress }) {
  const { joined, threshold, remaining, reward_active, reward_ever_granted, reward_expires_at, reward_days } = progress;
  const expiryDate = reward_expires_at
    ? new Date(reward_expires_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
    : null;

  // Active state — Silver granted
  if (reward_active) {
    return (
      <div
        className="mt-8 bg-brand-ink text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden"
        data-testid="invite-reward-active-banner"
      >
        <div className="absolute -right-10 -top-10 opacity-15">
          <Crown size={200} weight="duotone" className="text-brand-gold" />
        </div>
        <div className="relative flex-1">
          <div className="text-[11px] uppercase tracking-[3px] text-brand-gold font-semibold flex items-center gap-2">
            <Crown size={14} weight="fill" /> Reward unlocked
          </div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 leading-tight">
            You've earned <em className="text-brand-gold">1 month of Silver</em> — complimentary.
          </h2>
          <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-2xl">
            {joined} of your invitations have joined WR Rishtey. Silver perks are active on your account
            {expiryDate && <> until <strong className="text-brand-gold">{expiryDate}</strong></>}.
          </p>
        </div>
      </div>
    );
  }

  // Expired state — encourage more or upgrade
  if (reward_ever_granted) {
    return (
      <div
        className="mt-8 bg-brand-bone border border-brand-line p-8 flex flex-col md:flex-row md:items-center gap-6"
        data-testid="invite-reward-expired-banner"
      >
        <Gift size={44} weight="duotone" className="text-brand-terracotta" />
        <div className="flex-1">
          <div className="label-eyebrow text-brand-terracotta">Referral rewards</div>
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-2 text-brand-ink leading-tight">
            Your first Silver gift has ended.
          </h2>
          <p className="mt-2 text-sm text-brand-muted">
            Keep introducing thoughtful people — every joiner strengthens our community.
          </p>
        </div>
      </div>
    );
  }

  // In-progress state — dots + count-up
  const dots = Array.from({ length: threshold }, (_, i) => i < joined);
  return (
    <div
      className="mt-8 bg-brand-gold/10 border border-brand-gold/30 p-8 md:p-10"
      data-testid="invite-reward-progress-banner"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Gift size={20} weight="duotone" className="text-brand-terracotta" />
            <div className="label-eyebrow text-brand-terracotta">Referral reward · Free for a limited time</div>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-3 text-brand-ink leading-tight">
            Invite <em className="text-brand-terracotta">{threshold} friends</em>, get <em className="text-brand-terracotta">{reward_days} days of Silver</em> — on us.
          </h2>
          <p className="mt-3 text-sm text-brand-muted max-w-xl leading-relaxed">
            When 3 of your invitations accept and register, we automatically activate Silver
            membership on your account for 30 days. Unlimited interests, contact reveal on accepted matches,
            and priority placement.
          </p>
        </div>

        {/* Circular progress */}
        <div className="flex flex-col items-center gap-3 min-w-[160px]">
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#E5E1D8" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="42" fill="none"
                stroke="#8C3A27" strokeWidth="6" strokeLinecap="round"
                strokeDasharray={`${Math.min(100, (joined / threshold) * 100) * 2.638} 264`}
                className="transition-all duration-700"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-serif text-4xl tracking-tighter text-brand-ink num-lining leading-none">{joined}</div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted mt-0.5">of {threshold}</div>
            </div>
          </div>
          <div className="text-xs text-brand-muted text-center">
            {remaining === 0 ? "Reward unlocking…" : `${remaining} more to unlock`}
          </div>
        </div>
      </div>

      {/* Dots */}
      <div className="flex items-center gap-2 mt-6">
        {dots.map((filled, i) => (
          <div
            key={`invite-dot-${i}`}
            data-testid={`invite-progress-dot-${i}`}
            className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
              filled ? "bg-brand-terracotta" : "bg-brand-line"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
